import React from 'react';

const subscriptionTerms = [
  'Subscriptions are billed on a recurring basis, as described at the time of purchase, until cancelled.',
  'Payments are processed by third-party providers. You authorize recurring charges.',
  'You may cancel at any time through your account settings or by contacting us. Cancellation takes effect at the end of the current billing period. No refunds are provided for partial periods unless required by law.',
  'We reserve the right to change pricing with advance notice. Continued use after notice constitutes acceptance.',
  'Access may be suspended for non-payment.',
];

const communityRules = [
  'Be respectful. No harassment, hate speech, spam, or illegal content.',
  'Do not share subscription content outside the community or with non-members.',
  'Do not promote competing services or solicit investments in an unauthorized manner.',
  'We may remove content or suspend accounts for violations.',
];

const TermsAndConditions: React.FC = () => {
  return (
    <main className="min-h-screen bg-white text-[#17243a]">
      <section className="border-b border-gray-200 bg-[#f7f6f1]">
        <div className="mx-auto px-4 md:px-10 lg:px-32 w-full py-16 lg:py-24">
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.28em] text-[#0B6054]">Legal</p>
          <h1 className="mb-5 text-4xl font-bold tracking-tight text-[#1a2942] sm:text-5xl lg:text-6xl">
            Terms and Conditions
          </h1>
          <p className="text-lg text-gray-700">Last Updated: October 9, 2026</p>
        </div>
      </section>

      <section className="mx-auto px-4 md:px-10 lg:px-32 w-full py-12 lg:py-16">
        <article className="prose prose-lg max-w-none prose-headings:text-[#1a2942] prose-p:text-gray-700 prose-li:text-gray-700">
          <p>
            These Terms and Conditions (“Terms”) govern your access to and use of thewealthyminer.com, the associated
            subscription research service, community, and related content (the “Service”) operated by The Wealthy Miner LLC
            (“we,” “us,” or “our”), a limited liability company incorporated in the State of Wyoming, United States.
          </p>
          <p>
            By accessing or using the Service, creating an account, or purchasing a subscription, you agree to these Terms.
            If you do not agree, do not use the Service.
          </p>

          <section className="mt-10">
            <h2 className="mb-4 text-2xl font-bold">1. Description of the Service</h2>
            <p>
              The Wealthy Miner provides subscription-based research, analysis, educational content, curated stock ideas,
              videos, interviews, and access to an investor community focused on junior mining stocks, primarily TSX and TSXV
              listed companies. Content is intended for informational and educational purposes only.
            </p>
            <div className="mt-6 rounded-2xl border border-[#d8c16a] bg-[#fff9df] p-6 sm:p-8">
              <h3 className="mb-3 text-xl font-bold text-[#1a2942]">Important Investment Disclaimer</h3>
              <p>
                The Service does not provide personalized investment advice, recommendations tailored to your individual
                circumstances, or any form of regulated financial, investment, tax, or legal advice. The Wealthy Miner LLC is
                not registered as an investment adviser, broker-dealer, portfolio manager, or dealer with the U.S. Securities
                and Exchange Commission, any U.S. state securities regulator, any Canadian securities commission, or any other
                regulatory body.
              </p>
              <p>
                Junior mining stocks involve substantial risk of loss, including the possible loss of your entire investment.
                Past performance, including any historical results of companies or strategies discussed, is not indicative of
                future results. All content is general in nature. You should conduct your own due diligence, consider your own
                financial situation, risk tolerance, and investment objectives, and consult qualified professionals before
                making any investment decisions. We make no representations or warranties regarding the accuracy, completeness,
                or suitability of any information provided.
              </p>
              <p className="mb-0">Nothing on the Service is an offer to sell, or a solicitation of an offer to buy, any security.</p>
            </div>
          </section>

          <section className="mt-10">
            <h2 className="mb-4 text-2xl font-bold">2. Eligibility and Accounts</h2>
            <p>
              You must be at least 18 years old and able to form a binding contract. You are responsible for maintaining the
              confidentiality of your account credentials and for all activity under your account. Provide accurate information
              and notify us of any unauthorized use.
            </p>
          </section>

          <section className="mt-10">
            <h2 className="mb-4 text-2xl font-bold">3. Subscriptions, Payments, and Cancellations</h2>
            <ul className="list-disc space-y-2 pl-6">
              {subscriptionTerms.map((term) => (
                <li key={term}>{term}</li>
              ))}
            </ul>
          </section>

          <section className="mt-10">
            <h2 className="mb-4 text-2xl font-bold">4. Intellectual Property</h2>
            <p>
              All content on the Service, including text, research, videos, graphics, logos, and software, is owned by The
              Wealthy Miner LLC or its licensors and protected by United States and international copyright and other
              intellectual property laws. You receive a limited, non-exclusive, non-transferable license to access and use the
              content for your personal, non-commercial use during your active subscription. You may not copy, distribute,
              modify, reverse engineer, publicly display, or create derivative works without our prior written permission.
            </p>
            <p>
              User-generated content, including posts and comments, remains yours, but you grant us a worldwide, royalty-free
              license to use, display, and distribute it in connection with the Service.
            </p>
          </section>

          <section className="mt-10">
            <h2 className="mb-4 text-2xl font-bold">5. Community Rules and Acceptable Use</h2>
            <p>When participating in the community:</p>
            <ul className="list-disc space-y-2 pl-6">
              {communityRules.map((rule) => (
                <li key={rule}>{rule}</li>
              ))}
            </ul>
          </section>

          <section className="mt-10">
            <h2 className="mb-4 text-2xl font-bold">6. Disclaimers and Limitation of Liability</h2>
            <p>
              THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE.” TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL
              WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p>
              TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, THE WEALTHY MINER LLC AND ITS MEMBERS, MANAGERS, OFFICERS,
              EMPLOYEES, CONTRACTORS, AND AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
              OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, OR INVESTMENT LOSSES ARISING FROM YOUR USE OF THE SERVICE OR
              RELIANCE ON ANY CONTENT, EVEN IF ADVISED OF THE POSSIBILITY.
            </p>
            <p>
              Our total liability in any matter related to the Service shall not exceed the amount you paid us in the twelve
              (12) months preceding the claim.
            </p>
          </section>

          <section className="mt-10">
            <h2 className="mb-4 text-2xl font-bold">7. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless The Wealthy Miner LLC and its members, managers, and affiliates from
              any claims, damages, losses, or expenses, including reasonable legal fees, arising from your use of the Service,
              violation of these Terms, or infringement of any third-party rights.
            </p>
          </section>

          <section className="mt-10">
            <h2 className="mb-4 text-2xl font-bold">8. Termination</h2>
            <p>
              We may suspend or terminate your access at any time for violation of these Terms or for any other reason, with or
              without notice. Upon termination, your right to use the Service ends immediately. Sections that by their nature
              should survive, including disclaimers, limitations of liability, and intellectual property, will survive.
            </p>
          </section>

          <section className="mt-10">
            <h2 className="mb-4 text-2xl font-bold">9. Governing Law and Dispute Resolution</h2>
            <p>
              These Terms are governed by the laws of the State of Wyoming and the federal laws of the United States applicable
              therein, without regard to conflict-of-law principles. Any dispute arising out of these Terms or the Service shall
              be brought exclusively in the state or federal courts located in Wyoming, and you consent to personal jurisdiction
              there.
            </p>
          </section>

          <section className="mt-10">
            <h2 className="mb-4 text-2xl font-bold">10. Changes to Terms</h2>
            <p>
              We may update these Terms. Material changes will be posted on the site with an updated date. Continued use
              constitutes acceptance.
            </p>
          </section>

          <section className="mt-10 rounded-2xl border border-gray-200 bg-[#f7f6f1] p-6 sm:p-8">
            <h2 className="mb-4 text-2xl font-bold">11. Contact</h2>
            <p>
              The Wealthy Miner LLC
              <br />
              A Wyoming limited liability company
              <br />
              Email:{' '}
              <a
                href="mailto:info@thewealthyminer.com"
                className="font-medium text-[#0B6054] underline underline-offset-4 hover:text-[#c9a227]"
              >
                info@thewealthyminer.com
              </a>
              <br />
              Website: thewealthyminer.com
            </p>
            <p className="mb-0">If any provision is found unenforceable, the remaining provisions remain in effect.</p>
          </section>
        </article>
      </section>
    </main>
  );
};

export default TermsAndConditions;
